import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import {
  Loader2,
  Mail,
  MessageSquare,
  RefreshCcw,
  Search,
  Smartphone,
  Pencil,
  FileText,
} from 'lucide-react';

type OutboxChannel = 'email' | 'whatsapp' | 'pwa' | 'unknown';

type OutboxRow = {
  id: string;
  tripId: string;
  tripNumber: string;
  jobId: string;
  jobNumber: string;
  channel: OutboxChannel;
  target: string;
  subject: string;
  message: string;
  status: string;
  providerStatus: string;
  deliveryMode: string;
  requiredAction: string;
  errorMessage: string;
  attemptCount: number;
  createdAt: string;
  updatedAt: string;
  dispatchedAt: string;
  resentFromId?: string;
  resentAt?: string;
  resendReason?: string;
  sample?: boolean;
};

const SAMPLE_OUTBOX_ROWS: OutboxRow[] = [
  {
    id: 'sample-email-1',
    tripId: 'sample-trip-1',
    tripNumber: 'TM-TRIP-001',
    jobId: 'sample-job-1',
    jobNumber: 'TM-JOB-001',
    channel: 'email',
    target: 'kevin@cargoerp.com',
    subject: 'e-POD for Job TM-JOB-001',
    message: 'Dear customer,\n\nAttached is the e-POD for Job TM-JOB-001.\nDriver: Kevin\nVehicle: OTC-DRV-01\nProof Photos: 1\n\nRegards,\nBoox Transport',
    status: 'queued',
    providerStatus: 'live_sent',
    deliveryMode: 'gmail_service_account',
    requiredAction: '',
    errorMessage: '',
    attemptCount: 1,
    createdAt: '2026-05-09T09:00:00.000Z',
    updatedAt: '2026-05-09T09:02:00.000Z',
    dispatchedAt: '2026-05-09T09:02:00.000Z',
    sample: true,
  },
  {
    id: 'sample-wa-1',
    tripId: 'sample-trip-1',
    tripNumber: 'TM-TRIP-001',
    jobId: 'sample-job-1',
    jobNumber: 'TM-JOB-001',
    channel: 'whatsapp',
    target: '+6597256088',
    subject: 'e-POD ready for TM-JOB-001',
    message: 'Your delivery for TM-JOB-001 has been completed. e-POD is ready and attached in email.',
    status: 'queued',
    providerStatus: 'live_sent',
    deliveryMode: 'whatsapp_service',
    requiredAction: '',
    errorMessage: '',
    attemptCount: 1,
    createdAt: '2026-05-09T09:03:00.000Z',
    updatedAt: '2026-05-09T09:03:30.000Z',
    dispatchedAt: '2026-05-09T09:03:30.000Z',
    sample: true,
  },
  {
    id: 'sample-pwa-1',
    tripId: 'sample-trip-2',
    tripNumber: 'TM-TRIP-002',
    jobId: 'sample-job-2',
    jobNumber: 'TM-JOB-002',
    channel: 'pwa',
    target: 'driver01@otc-asia',
    subject: 'Delivery completed for TM-JOB-002',
    message: 'Trip TM-TRIP-002 is completed. Review checklist, surcharge, and supporting photo before final close.',
    status: 'queued',
    providerStatus: 'live_sent',
    deliveryMode: 'pwa_in_app_and_web_push',
    requiredAction: '',
    errorMessage: '',
    attemptCount: 1,
    createdAt: '2026-05-09T09:10:00.000Z',
    updatedAt: '2026-05-09T09:10:15.000Z',
    dispatchedAt: '2026-05-09T09:10:15.000Z',
    sample: true,
  },
];

function str(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = obj[key];
    if (value !== null && value !== undefined && value !== '') return String(value);
  }
  return '';
}

function arr(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((item) => item && typeof item === 'object') as Record<string, unknown>[]
    : [];
}

function formatDateTime(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-SG', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function channelIcon(channel: OutboxChannel) {
  if (channel === 'email') return Mail;
  if (channel === 'whatsapp') return MessageSquare;
  return Smartphone;
}

function channelTone(channel: OutboxChannel): string {
  if (channel === 'email') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (channel === 'whatsapp') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (channel === 'pwa') return 'bg-purple-50 text-purple-700 border-purple-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
}

function statusTone(value: string): string {
  const normalized = value.toLowerCase();
  if (['live_sent', 'sent', 'provider_queued'].includes(normalized)) return 'bg-green-50 text-green-700 border-green-200';
  if (['queued', 'approval_required', 'missing_target', 'provider_not_configured'].includes(normalized)) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (['send_failed', 'invalid_recipient', 'missing_recipient'].includes(normalized)) return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
}

function normalizeOutboxChannel(value: string): OutboxChannel {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'email' || normalized === 'whatsapp' || normalized === 'pwa') return normalized;
  return 'unknown';
}

export default function TmsOutboxPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<OutboxRow[]>([]);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string>('');
  const [resending, setResending] = useState(false);

  const loadOutbox = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ items?: unknown[] }>('/job-trips?limit=100&sort=updated_at&order=desc');
      const rows = ((res as { items?: unknown[] }).items ?? []) as Record<string, unknown>[];
      const nextItems = rows.flatMap((row) => {
        const tripId = str(row, 'id');
        const tripNumber = str(row, 'trip_number', 'trip_code', 'tripCode', 'code');
        const jobId = str(row, 'job_id', 'jobId');
        const jobNumber = str(row, 'job_number', 'jobNumber');
        return arr(row.epod_outbox).map((entry, index) => ({
          id: str(entry, 'id') || `${tripId}:${index}`,
          tripId,
          tripNumber,
          jobId,
          jobNumber,
          channel: normalizeOutboxChannel(str(entry, 'channel')),
          target: str(entry, 'target'),
          subject: str(entry, 'subject'),
          message: str(entry, 'message'),
          status: str(entry, 'status') || 'queued',
          providerStatus: str(entry, 'provider_status', 'providerStatus'),
          deliveryMode: str(entry, 'delivery_mode', 'deliveryMode'),
          requiredAction: str(entry, 'required_action', 'requiredAction'),
          errorMessage: str(entry, 'error_message', 'errorMessage'),
          attemptCount: Number(entry.attempt_count ?? entry.attemptCount ?? 0),
          createdAt: str(entry, 'created_at', 'createdAt'),
          updatedAt: str(entry, 'updated_at', 'updatedAt'),
          dispatchedAt: str(entry, 'dispatched_at', 'dispatchedAt'),
          resentFromId: str(entry, 'resent_from_id', 'resentFromId'),
          resentAt: str(entry, 'resent_at', 'resentAt'),
          resendReason: str(entry, 'resend_reason', 'resendReason'),
        }));
      });
      const effectiveItems = nextItems.length > 0 ? nextItems : SAMPLE_OUTBOX_ROWS;
      setItems(effectiveItems);
      setSelectedId((current) => current && effectiveItems.some((item) => item.id === current) ? current : (effectiveItems[0]?.id ?? ''));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOutbox();
  }, [loadOutbox]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (channelFilter !== 'all' && item.channel !== channelFilter) return false;
      const effectiveStatus = item.providerStatus || item.status;
      if (statusFilter !== 'all' && effectiveStatus !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return [
        item.jobNumber,
        item.tripNumber,
        item.target,
        item.subject,
        item.message,
      ].some((value) => value.toLowerCase().includes(q));
    });
  }, [channelFilter, items, search, statusFilter]);

  const selectedItem = filteredItems.find((item) => item.id === selectedId) ?? filteredItems[0] ?? null;

  useEffect(() => {
    if (selectedItem && selectedId !== selectedItem.id) {
      setSelectedId(selectedItem.id);
    }
  }, [selectedId, selectedItem]);

  const handleResend = useCallback(async () => {
    if (!selectedItem || selectedItem.sample) return;
    setResending(true);
    try {
      await api.post(`/job-trips/${selectedItem.tripId}/epod/dispatch`, {
        channels: [selectedItem.channel],
        approveExternalSend: selectedItem.channel !== 'pwa',
        retryClosed: true,
        resendReason: `Manual resend from TM Outbox (${selectedItem.channel})`,
      });
      toast.success(`${selectedItem.channel} resent and outbox history preserved.`);
      await loadOutbox();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to resend outbox item');
    } finally {
      setResending(false);
    }
  }, [loadOutbox, selectedItem]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-8 pl-8 text-sm"
            placeholder="Search job, trip, recipient, subject..."
          />
        </div>
        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="h-8 w-[130px] text-xs">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Channels</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="pwa">PWA</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[150px] text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="live_sent">Live Sent</SelectItem>
            <SelectItem value="provider_queued">Provider Queued</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="send_failed">Send Failed</SelectItem>
            <SelectItem value="approval_required">Approval Required</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8" onClick={() => void loadOutbox()}>
          <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">TM Outbox</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-muted-foreground">
              {loading ? 'Loading outbox...' : `${filteredItems.length} item(s) in TM outbox`}
              {!loading && items.some((item) => item.sample) ? ' · showing sample rows because tenant outbox is empty' : ''}
            </div>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[920px] text-sm">
                <thead className="border-b bg-muted/30">
                  <tr>
                    {['Channel', 'Job / Trip', 'Recipient', 'Subject', 'Status', 'Updated', ''].map((label) => (
                      <th key={label} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-xs text-muted-foreground">
                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                        Loading...
                      </td>
                    </tr>
                  ) : filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-xs text-muted-foreground">
                        No outbox item found
                      </td>
                    </tr>
                  ) : filteredItems.map((item) => {
                    const ChannelIcon = channelIcon(item.channel);
                    const effectiveStatus = item.providerStatus || item.status;
                    const isActive = selectedItem?.id === item.id;
                    return (
                      <tr
                        key={item.id}
                        className={`border-b last:border-0 cursor-pointer hover:bg-muted/20 ${isActive ? 'bg-muted/30' : ''}`}
                        onClick={() => setSelectedId(item.id)}
                      >
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge variant="outline" className={`gap-1 text-[10px] ${channelTone(item.channel)}`}>
                              <ChannelIcon className="h-3 w-3" />
                              {item.channel}
                            </Badge>
                            {item.sample ? (
                              <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
                                sample
                              </Badge>
                            ) : null}
                            {item.resentFromId ? (
                              <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200">
                                resend
                              </Badge>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-xs">
                          <div className="font-medium">{item.jobNumber || '-'}</div>
                          <div className="text-muted-foreground">{item.tripNumber || '-'}</div>
                        </td>
                        <td className="px-3 py-3 text-xs">
                          <div className="max-w-[180px] truncate">{item.target || '-'}</div>
                        </td>
                        <td className="px-3 py-3 text-xs">
                          <div className="max-w-[240px] truncate font-medium">{item.subject || '-'}</div>
                          <div className="mt-0.5 max-w-[240px] truncate text-muted-foreground">{item.message || '-'}</div>
                        </td>
                        <td className="px-3 py-3 text-xs">
                          <Badge variant="outline" className={`text-[10px] ${statusTone(effectiveStatus)}`}>
                            {effectiveStatus || '-'}
                          </Badge>
                        </td>
                        <td className="px-3 py-3 text-xs text-muted-foreground">
                          {formatDateTime(item.dispatchedAt || item.updatedAt || item.createdAt)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Button variant="ghost" size="sm" className="h-7 text-xs" asChild onClick={(event) => event.stopPropagation()}>
                            <Link to={`/tm/jobs/${item.jobId}`}>Open</Link>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Message Detail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedItem ? (
              <div className="text-sm text-muted-foreground">Select an outbox item to inspect the content.</div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={`text-[10px] ${channelTone(selectedItem.channel)}`}>{selectedItem.channel}</Badge>
                  <Badge variant="outline" className={`text-[10px] ${statusTone(selectedItem.providerStatus || selectedItem.status)}`}>
                    {selectedItem.providerStatus || selectedItem.status || '-'}
                  </Badge>
                  {selectedItem.deliveryMode ? (
                    <Badge variant="outline" className="text-[10px]">{selectedItem.deliveryMode}</Badge>
                  ) : null}
                </div>

                <div className="grid gap-2 text-xs">
                  <div><span className="text-muted-foreground">Job:</span> <span className="font-medium">{selectedItem.jobNumber || '-'}</span></div>
                  <div><span className="text-muted-foreground">Trip:</span> <span className="font-medium">{selectedItem.tripNumber || '-'}</span></div>
                  <div><span className="text-muted-foreground">Recipient:</span> <span className="font-medium">{selectedItem.target || '-'}</span></div>
                  <div><span className="text-muted-foreground">Subject:</span> <span className="font-medium">{selectedItem.subject || '-'}</span></div>
                  <div><span className="text-muted-foreground">Attempts:</span> <span className="font-medium">{selectedItem.attemptCount}</span></div>
                  <div><span className="text-muted-foreground">Updated:</span> <span className="font-medium">{formatDateTime(selectedItem.updatedAt || selectedItem.createdAt)}</span></div>
                  {selectedItem.resentFromId ? (
                    <div><span className="text-muted-foreground">Resent:</span> <span className="font-medium">{formatDateTime(selectedItem.resentAt || selectedItem.createdAt)}</span></div>
                  ) : null}
                </div>

                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">Message</div>
                  <div className="max-h-[360px] overflow-auto rounded-lg border bg-muted/20 p-3 text-xs whitespace-pre-wrap">
                    {selectedItem.message || '-'}
                  </div>
                </div>

                {selectedItem.requiredAction || selectedItem.errorMessage ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    {selectedItem.requiredAction || selectedItem.errorMessage}
                  </div>
                ) : null}

                {selectedItem.resendReason ? (
                  <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-xs text-orange-800">
                    {selectedItem.resendReason}
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" asChild>
                    <Link to={`/tm/jobs/${selectedItem.jobId}`}>
                      <FileText className="mr-1.5 h-3.5 w-3.5" />
                      Open Job
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/tm/jobs/${selectedItem.jobId}/edit`}>
                      <Pencil className="mr-1.5 h-3.5 w-3.5" />
                      Edit Job
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => void handleResend()} disabled={resending || selectedItem.sample}>
                    {resending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />}
                    Resend
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
